import SkeletonTextLine from "@/components/SkeletonTextLine";
import PatientStatus from "@/model/PatientStatus";
import cn from "@/util/cn";
import { DateTimeFormatter, Instant, ZoneId } from "@js-joda/core";
import { Locale } from "@js-joda/locale";
import { ComponentPropsWithoutRef, Fragment } from "react";

type SummarySinceLastReviewBannerProps = {
  patientStatus: PatientStatus | undefined;
  lastReviewTime: Instant | undefined;
  unreviewedReadingsSummaryData: {
    amount: number;
    classificationText?: string;
    className?: string;
  }[];
  isLoading: boolean;
} & ComponentPropsWithoutRef<"div">;

const SummarySinceLastReviewBanner = ({
  patientStatus,
  lastReviewTime,
  unreviewedReadingsSummaryData,
  isLoading,
  className,
  ...otherProps
}: SummarySinceLastReviewBannerProps) => {
  const sinceText = lastReviewTime
    ? `Since the last review on ${dateTimeFormatter.format(lastReviewTime.atZone(ZoneId.SYSTEM).toLocalDateTime())}, the patient had `
    : "Since the patient started monitoring, the patient had ";
  const measurementsText = (measurements: number) =>
    measurements === 1 ? "measurement" : "measurements";
  const summaryData = unreviewedReadingsSummaryData.filter(({ amount }) => amount > 0);

  return (
    <div
      className={cn("rounded-lg border border-gray-200 bg-gray-50 p-4", className)}
      {...otherProps}
    >
      {isLoading ? (
        <SkeletonTextLine className="my-1 h-4 w-1/2" />
      ) : (
        <p className="text-primary/90">
          {patientStatus === "onboarding" ? (
            "The patient has not taken any measurements"
          ) : (
            <>
              {sinceText}
              {summaryData.length == 0
                ? "no new measurements"
                : summaryData.map(({ amount, classificationText, className }, index) => (
                    <Fragment key={`${amount}-${classificationText}`}>
                      <span className={cn("font-bold", className)}>
                        {amount}
                        {classificationText && ` ${classificationText}`}
                      </span>
                      {index == summaryData.length - 1
                        ? ` ${measurementsText(amount)}`
                        : index == summaryData.length - 2
                          ? ", and "
                          : ", "}
                    </Fragment>
                  ))}
            </>
          )}
        </p>
      )}
    </div>
  );
};

const dateTimeFormatter = DateTimeFormatter.ofPattern("MMMM d 'at' h:mm a").withLocale(Locale.US);

export default SummarySinceLastReviewBanner;
